import type { PbjLeverCutLimit, PbjPrediction } from "../../../utils/models";
import { MAX_CUT_SHARE_PER_LEVER, maxCutSpend } from "./recMath.utils";
import { type RecommendationType, WAGE_OVERRIDE_FIELDS, type WageKey, type WageOverrideField } from "./recommendations.constants";

// Slider granularity in its spend units ($/applicable-PPD).
export const SPEND_STEP = 0.01;

// The cut side of a lever's slider is usually tiny next to the add side (a 25%-of-current-hours cut
// vs. a 3 HPPD add — e.g. −$4.63 vs +$143.61 for one RN lever, 3% of the rail). Giving each side its
// own scale with at least this share of the rail keeps the cut range grabbable.
export const CUT_SIDE_MIN_SHARE = 0.3;

// HPPD figures on the card are truncated (toward zero) to 3 decimals so a projection never reads
// as more than the hours deliver.
export const formatHppdMagnitude = (magnitude: number): string => (Math.floor(magnitude * 1000) / 1000).toFixed(3);

// Copy for the note shown when the slider sits at its cut floor — names the rule that binds. "none"
// means the role has no worked hours in the period, so there is nothing to cut and the note says so.
export const cutLimitKey = (limitedBy: PbjLeverCutLimit["limitedBy"]): string => {
	switch (limitedBy) {
		case "rn_floor":
			return "pbj_model.recommendations.cut_limit_rn_floor";
		case "share":
			return "pbj_model.recommendations.cut_limit_share";
		case "none":
			return "pbj_model.recommendations.cut_limit_none";
	}
};

// Interpolation for every cut-limit note: the share cap as a whole percent ("25%"), from the FE mirror of
// the backend constant, so the copy can never name a different cap from the one the slider enforces.
export const CUT_LIMIT_COPY_VALUES = { share: Math.round(MAX_CUT_SHARE_PER_LEVER * 100) } as const;

// wageKey → the override field that pins it. Lets each rec's wage tooltip report
// whether the wage shown is a manually-set override or the computed assumption.
export const OVERRIDE_KEY_BY_WAGE_KEY = Object.fromEntries(WAGE_OVERRIDE_FIELDS.map((f) => [f.computedKey, f.overrideKey])) as Record<
	WageKey,
	WageOverrideField["overrideKey"]
>;

// Base i18n stem per role; an overridden wage appends "_override" to swap the
// "Assumed …" copy for the "manually set" variant.
export const wageTooltipKey = (rec: RecommendationType, isOverride: boolean): string => {
	const role = rec.wageKey === "rn" || rec.wageKey === "weekendRn" ? "rn" : rec.wageKey === "lpn" || rec.wageKey === "weekendLpn" ? "lpn" : "cna";
	return `pbj_model.recommendations.wage_tooltip.${role}${isOverride ? "_override" : ""}`;
};

// Where a lever's slider bottoms out on its cut side, and whether the thumb is there now (within half a
// step). A lever that can't be cut at all (a 0 floor: an RN role already at or under the floor, or a
// role with no worked hours) is "at its floor" whenever the thumb sits at 0 or below, so its note
// explains the dead cut side instead of leaving it unexplained. `copyKey` names the rule that stops it
// (null only when the backend sent no limit — a period that hasn't started); resolved even off the
// floor so the note keeps its text while it animates out. Shared by the slider (its minimum) and the
// row (the note under it).
export const cutFloorState = (
	rec: RecommendationType,
	prediction: PbjPrediction,
	wage: number,
	census: number,
	currentSpend: number,
): { cutFloor: number; atCutFloor: boolean; copyKey: string | null; maxCutHoursPerDay: number } => {
	const cutFloor = maxCutSpend(rec, prediction, wage, census);
	const limit = prediction.cutLimits[rec.id];
	return {
		cutFloor,
		atCutFloor: currentSpend <= -cutFloor + SPEND_STEP / 2,
		copyKey: limit ? cutLimitKey(limit.limitedBy) : null,
		// A missing limit means the lever can't be cut, identical to a 0-hour limit (see `maxCutSpend`).
		maxCutHoursPerDay: limit ? limit.maxCutHoursPerDay : 0,
	};
};
